import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { fetchFreshData, SheetsFetchError } from '../data';
import type { SheetsResult } from '../data';
import type { MarketDataErrorKind } from '../context/MarketDataContext.utils';
import { VENDORS_SWR_KEY } from '../context/MarketDataContext';

export interface UseVendorsReturn {
  data: SheetsResult | undefined;
  loading: boolean;
  revalidating: boolean;
  error: string | null;
  errorKind: MarketDataErrorKind | null;
  refresh: () => void;
  dismissError: () => void;
}

/**
 * SWR-powered hook for vendor server state.
 *
 * Exposes `loading`/`revalidating`/`error`/`errorKind` that were previously
 * tracked in MarketDataContext, plus `refresh` and `dismissError` actions.
 *
 * The SWR key is shared with MarketDataProvider so the two deduplicate to a
 * single network request, but `errorVisible` (which drives dismissError) is
 * local state. Intended for a single consumer (AppShell). If a second
 * consumer ever needs `dismissError`, hoist `errorVisible` to a context so
 * dismiss is global.
 */
export function useVendors(): UseVendorsReturn {
  const [errorVisible, setErrorVisible] = useState(false);

  const { data, error, isValidating, mutate } = useSWR<SheetsResult, Error>(
    VENDORS_SWR_KEY,
    fetchFreshData,
    {
      // Match MarketDataProvider's config so neither hook triggers a background
      // revalidation the other doesn't expect. Without these, a browser tab-out/
      // tab-in fires focus revalidation here, propagates through the shared SWR
      // cache, and causes MarketDataContext to dispatch SET_DATA — clobbering any
      // unsaved editor drags. See: github.com/eriksaulnier/market-map/issues/144
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: () => setErrorVisible(true),
      onSuccess: () => setErrorVisible(false),
    },
  );

  const loading = !data && !error;
  const revalidating = isValidating && !!data;
  const activeError = errorVisible ? (error ?? null) : null;

  const refresh = useCallback(() => { void mutate(); }, [mutate]);
  const dismissError = useCallback(() => setErrorVisible(false), []);

  return {
    data,
    loading,
    revalidating,
    error: activeError?.message ?? null,
    errorKind: activeError instanceof SheetsFetchError
      ? activeError.kind
      : activeError ? 'unknown' : null,
    refresh,
    dismissError,
  };
}
